import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/common/components/Badge/Badge';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { CardActionGroup } from '@/common/components/CardActionGroup/CardActionGroup';
import { Icon } from '@/common/components/Icons/Icon';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { ITMealPaymentStrategyLearningCenter } from '@/common/containers/Dashboard/ITMealPaymentStrategyLearningCenter/ITMealPaymentStrategyLearningCenter';
import { useGetMealPaymentStrategy } from '@/common/containers/Dashboard/MealVoucher/MealVoucher.utils';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { TMealPaymentStrategy } from '@/common/integrations/menhir/employee/meal/meal.types';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { useRouter } from 'expo-router';

export const ITMealPaymentStrategyPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const mealPaymentStrategy = useGetMealPaymentStrategy();
  const [learningCenterType, setLearningCenterType] =
    useState<TMealPaymentStrategy>();

  usePageViewTracking('single_transaction_select_payment_method_page', {
    current_strategy: mealPaymentStrategy,
  });

  const selectedBadge = (
    <Badge status="success" emphasis="soft">
      {t('general.labels.selected')}
    </Badge>
  );
  const onCloseLearningCenter = () => {
    setLearningCenterType(undefined);

    switch (learningCenterType) {
      case 'card':
        router.push(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
        break;
      case 'credits':
        router.push(ROUTES.CREDITS_ADD);
        break;
    }
  };

  return (
    <PageLayout
      headerProps={{
        title: t('dashboard.it.meal.payment_strategy.page.title'),
        description: t('dashboard.it.meal.payment_strategy.page.description'),
        backButton: true,
        infoButton: {
          onPress: () => setLearningCenterType('none'),
        },
      }}
    >
      <CardActionGroup>
        <CardAction
          title={t(
            'dashboard.it.meal.payment_strategy.page.option.automatic.title'
          )}
          description={t(
            'dashboard.it.meal.payment_strategy.page.option.automatic.description'
          )}
          onPress={() => {
            AnalyticsService.trackCustomEvent(
              'single_transaction_select_payment_method_page_card_button_clicked',
              { current_strategy: mealPaymentStrategy }
            );
            if (mealPaymentStrategy === 'none') {
              setLearningCenterType('card');
              return;
            }
            router.push(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
          }}
          action={
            <Stack horizontal gap="sm" align="center">
              {mealPaymentStrategy === 'card' && selectedBadge}
              <Icon icon="chevron-right" color="neutral20" />
            </Stack>
          }
        />
        <CardAction
          title={t(
            'dashboard.it.meal.payment_strategy.page.option.manual.title'
          )}
          description={t(
            'dashboard.it.meal.payment_strategy.page.option.manual.description'
          )}
          onPress={() => {
            AnalyticsService.trackCustomEvent(
              'single_transaction_select_payment_method_page_credits_button_clicked',
              { current_strategy: mealPaymentStrategy }
            );
            if (mealPaymentStrategy === 'none') {
              setLearningCenterType('credits');
              return;
            }
            router.push(ROUTES.CREDITS_ADD);
          }}
          action={
            <Stack horizontal gap="sm" align="center">
              {mealPaymentStrategy === 'credits' && selectedBadge}
              <Icon icon="chevron-right" color="neutral20" />
            </Stack>
          }
        />
      </CardActionGroup>
      {learningCenterType && (
        <ITMealPaymentStrategyLearningCenter
          type={learningCenterType}
          onClose={onCloseLearningCenter}
        />
      )}
    </PageLayout>
  );
};
